/**
 * 万里汇（WorldFirst）合作落地页 —— 图片静态映射
 *
 * 需求来源：docs/万里汇合作落地页/万里汇合作落地页-提示词.md 第1次提交 需求4（任务单 DEV-124230，父单 DEV-124227）。
 *
 * 注意事项：
 * 1. 原型 27 张图原为远程外链（cjec / cc-west-usa / frontend.cjdropshipping / mdn.marmot-cloud），
 *    已下载并按用途语义化命名，落到公共资源仓 `static-resource-platform/resources/egg/worldfirst/`，
 *    随该仓自身流水线发布（dev 分支→测试环境，master→正式）。
 * 2. 取址方式与 /odm、/alternatives、/cj-business-travel 同款：按 cdnHost 拼 CDN 路径，
 *    不走 webpack 的 asset 别名 require（图片不在本仓内）。
 *    cdnHost 由 fetch.js 从 config.view.variable.CDN_HOST 取得并经 pageData 下传。
 * 3. 【注意】本地开发 cdnHost **不是空串**：config/config.default.js:9 写死
 *    `const devOssHost = commonConfig.ossHost["test"]`、:86 `CDN_HOST: devOssHost`，
 *    即本地 dev 指向**测试环境 OSS 桶**。所以图片必须先合入资源仓 dev 分支发到测试环境，
 *    本地才看得到图 —— 不是"本地退化为同源相对路径"。
 *    （app/web/page/cjBusinessTravel/images.js:12 那句"本地开发下 cdnHost 为空串"是过期表述，勿照抄。）
 * 4. 【格式选择的判据，改图时按此办】资源仓全树 0 个 webp（既有格式 jpg/png/jpeg/svg），
 *    故 17 张 webp 源图一律转码：
 *      · 真实透明（整图 38%~52% 像素全透明的抠图类）→ PNG，保留 alpha；
 *      · 99% 不透明、仅 0.33% 抗锯齿圆角边缘的照片（business_*）→ 按容器底色 #eeeeee 合成后转
 *        progressive JPEG。圆角本来就由 CSS 负责（.wf-business-image-wrap 的
 *        `overflow:hidden; border-radius:24px; background:#eeeeee`），图片自带的边缘 alpha 是冗余的，
 *        当成透明图去量化调色板反而会让照片出现色带。
 *      · 不透明照片/背景（event_offline* / faq_bg / hero_banner_bg）→ progressive JPEG。
 * 5. 【体积】单张目标 ≤150KB；硬闸 200KB 由资源仓 bin/index.ts:92 的
 *    `limitImageSize:{size:1024*200}` 代码强制，**超限会让整仓发布失败**（不只是本页）。
 *    资源仓 README:17 写的 100k 是过时口径（实测现存最大 200847B）。
 */
'use strict';

var DIR = '/resources/egg/worldfirst/';

/**
 * 文件名 → 用途对照（改图时按用途定位，不必回溯原型文件名）。
 * 左侧即资源仓内的实际文件名，与 FILES 一一对应。
 */
var USAGE = {
  // §1 Hero（cjwf-dark-banner）
  'hero_banner_bg.jpg': 'Hero 区块 CSS 背景大图（opacity .52，压在粉→黑渐变之下）',
  // §2 Bonus（cjwf-bonus）
  'bonus_pay_cj_worldfirst.png': 'Bonus 左卡配图 —— 通过 WorldFirst 付款给 CJ',
  'bonus_world_card_spend.png': 'Bonus 右卡配图 —— World Card 累计消费',
  'bonus_reward_credited.png': 'Bonus reward 条配图 —— 奖励入账',
  // §3 Steps（wf-steps-section）
  'step_create_account.png': 'Step 1 图标 —— 创建 WorldFirst 账户',
  'step_use_account.png': 'Step 2 图标 —— 开始使用账户',
  'step_get_bonus.png': 'Step 3 图标 —— 领取 $100 奖励',
  // §4 Business（wf-business-section）
  'business_easy_collection.jpg': 'Business 卡1 配图 —— Easy collection',
  'business_lower_fees.jpg': 'Business 卡2 配图 —— Lower fees. More savings',
  'business_pay_cj_fast.jpg': 'Business 卡3 配图 —— Pay CJ fast and easily',
  // §5 Multi-currency（wf-multi-section）
  'multi_local_receiving_accounts.png': 'Multi 卡1 —— Local receiving accounts',
  'multi_competitive_fx_rates.png': 'Multi 卡2 —— Competitive FX rates, 24/7',
  'multi_free_account_setup.png': 'Multi 卡3 —— Free, easy account setup',
  'multi_global_payments.png': 'Multi 卡4 —— Global payments（原型标题与卡1 重复，已按 D7③ 修正）',
  'multi_marketplace_collections.png': 'Multi 卡5 —— 130+ marketplace collections',
  'multi_card_payments.png': 'Multi 卡6 —— Save with card payments',
  // §6 Events 轮播（cj-suggestion-slider）—— 顺序即卡片 01~08 的渲染顺序
  'event_offline01.jpg': 'Events 轮播 01',
  'event_offline02.jpg': 'Events 轮播 02',
  'event_offline03.jpg': 'Events 轮播 03',
  'event_offline04.jpg': 'Events 轮播 04',
  'event_offline05.jpg': 'Events 轮播 05',
  'event_offline06.jpg': 'Events 轮播 06',
  'event_offline07.jpg': 'Events 轮播 07',
  'event_offline08.jpg': 'Events 轮播 08',
  // §7 FAQ（cjwf-premium-faq）
  'faq_bg.jpg': 'FAQ 区块背景图',
  // §8 底部 CTA（cjwf-cta）
  'logo_cjdropshipping.svg': '底部 CTA 左侧品牌浮标 —— CJdropshipping logo',
  'logo_worldfirst.svg': '底部 CTA 右侧品牌浮标 —— WorldFirst logo（原在第三方 CDN mdn.marmot-cloud，已落仓）',
};

/** 全部文件名（顺序即 USAGE 声明顺序），供单测校验实体齐备与命名规则。 */
var FILES = Object.keys(USAGE);

/**
 * 生成本页图片取址器。
 * @param {string} [cdnHost] CDN 域名（本地 dev 为测试环境 OSS 桶，见文件头注释第 3 条）
 * @returns {Function} img(name) → 完整 URL
 */
function createImg(cdnHost) {
  var base = (cdnHost || '') + DIR;
  return function img(name) {
    return base + name;
  };
}

/**
 * 仅取 URL（CSS 背景变量等无需 <img> 标签的场景）。
 * 【为什么需要】原型有 2 张图是写在 CSS 里的 background-image（Hero 底图 L75、FAQ 底图），
 * 而 less 在构建期拿不到 cdnHost（它是运行时按环境注入的）。故 index.less 里写成
 * `url(var(--wf-hero-bg))` / `url(var(--wf-faq-bg))`，由 index.jsx 用本函数算出实际地址、
 * 以内联 CSS 自定义属性写到页面根节点上。
 * 不这么做的后果：CSS 里会残留原型的**第三方远程地址**，图片绕过公共资源仓直接打到
 * cc-west-usa / cjec，既不受控也违背需求4「图片要落到 resources/egg/worldfirst」。
 *
 * @param {string} [cdnHost] CDN 域名
 * @param {string} name 文件名
 * @returns {string} 完整 URL
 */
function url(cdnHost, name) {
  return (cdnHost || '') + DIR + name;
}

module.exports = { DIR: DIR, FILES: FILES, USAGE: USAGE, createImg: createImg, url: url };
